import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GripVertical, Trash2, ChevronDown } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertEscalationPolicyPayload,
  AlertEscalationPolicyResponse,
  EscalationStep,
  SelectableMember,
  createEscalationPolicy,
  getCurrentPrimary,
  getEscalationPolicy,
  getSelectableMembers,
  upsertEscalationPolicy,
} from "@/api/escalationPolicy";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { useTeamStore } from "@/app/store/useTeamStore";
import { toast } from "@/shared/hooks/useToast";
import { ApiError } from "@/shared/libs/axiosClient";

// ── types ────────────────────────────────────────────────────────────────────

interface StepDraft {
  uid: string; // local key for dnd
  isPrimary: boolean; // step1: 로테이션 primary 자동 / step2+: false
  memberIds: number[]; // step2+ 직접 선택 멤버 (primary step은 빈 배열)
  interval_minutes: number;
  max_attempts: number;
}

interface QuietSnapshot {
  enabled: boolean;
  start: string;
  end: string;
  resumeAt: string;
}

function toDraft(step: EscalationStep, uid: string, isFirst: boolean): StepDraft {
  // Level 1(첫 단계)은 항상 로테이션 primary로 정규화한다.
  // 구포맷({role:"primary"})·신포맷({is_primary}) 모두, 그리고 마이그레이션 전 데이터도 안전하게 처리.
  if (isFirst) {
    return {
      uid,
      isPrimary: true,
      memberIds: [],
      interval_minutes: step.interval_minutes,
      max_attempts: step.max_attempts,
    };
  }
  const memberIds = step.alert_targets
    .map((t) => t.member_id)
    .filter((id): id is number => id != null);
  return {
    uid,
    isPrimary: false,
    memberIds,
    interval_minutes: step.interval_minutes,
    max_attempts: step.max_attempts,
  };
}

function toApiStep(draft: StepDraft, index: number): EscalationStep {
  const alert_targets = draft.isPrimary
    ? [{ is_primary: true }]
    : draft.memberIds.map((member_id) => ({ member_id }));
  return {
    step: index + 1,
    alert_targets,
    interval_minutes: draft.interval_minutes,
    max_attempts: draft.max_attempts,
  };
}

function newUid() {
  return crypto.randomUUID();
}

function makePrimaryDraft(): StepDraft {
  return { uid: newUid(), isPrimary: true, memberIds: [], interval_minutes: 2, max_attempts: 3 };
}

// ── MemberAvatar (구글 프로필 이미지 + 이니셜 폴백) ──────────────────────────
const AVATAR_COLORS = [
  "bg-red-500", "bg-orange-500", "bg-amber-500", "bg-lime-500",
  "bg-green-500", "bg-emerald-500", "bg-teal-500", "bg-cyan-500",
  "bg-sky-500", "bg-blue-500", "bg-indigo-500", "bg-violet-500",
  "bg-purple-500", "bg-fuchsia-500", "bg-pink-500", "bg-rose-500",
];

interface MemberInfo {
  id: number;
  name: string;
  picture?: string | null;
}

function MemberAvatar({ member }: { member: MemberInfo }) {
  const [imgFailed, setImgFailed] = useState(false);
  const color = AVATAR_COLORS[member.id % AVATAR_COLORS.length];
  const initials =
    member.name
      .split(" ")
      .map((n) => n[0] ?? "")
      .join("")
      .toUpperCase()
      .slice(0, 2) || "?";

  if (member.picture && !imgFailed) {
    return (
      <img
        src={member.picture}
        alt={member.name}
        title={member.name}
        className="h-5 w-5 rounded-full object-cover shrink-0"
        onError={() => setImgFailed(true)}
      />
    );
  }
  return (
    <div
      className={`h-5 w-5 rounded-full ${color} flex items-center justify-center text-white text-[10px] font-semibold shrink-0`}
      title={member.name}
    >
      {initials}
    </div>
  );
}

// ── StepCard (sortable) ───────────────────────────────────────────────────────

interface StepCardProps {
  draft: StepDraft;
  level: number;
  memberName: (id: number) => string;
  memberInfo: (id: number) => MemberInfo;
  primaryName: string | null;
  primaryMember: MemberInfo | null;
  onDelete: () => void;
  onClick: () => void;
  canDelete: boolean;
}

function StepCard({
  draft,
  level,
  memberName,
  memberInfo,
  primaryName,
  primaryMember,
  onDelete,
  onClick,
  canDelete,
}: StepCardProps) {
  // Level 1(primary)은 항상 최상단 고정 — 드래그 소스/드롭 대상에서 제외
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: draft.uid, disabled: draft.isPrimary });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      <div className="border rounded-lg bg-card hover:border-primary/50 transition-colors">
        {/* header */}
        <div className="flex items-center gap-2 p-4 pb-0">
          {draft.isPrimary ? (
            <span
              aria-hidden="true"
              title="Level 1은 고정입니다"
              className="text-muted-foreground/40 cursor-not-allowed shrink-0"
            >
              <GripVertical className="h-5 w-5" />
            </span>
          ) : (
            <button
              type="button"
              aria-label="단계 순서 변경"
              className="touch-none cursor-grab text-muted-foreground hover:text-foreground shrink-0"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-5 w-5" />
            </button>
          )}
          <button
            type="button"
            className="font-semibold text-sm flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
            onClick={onClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }}
          >
            Level {level}
          </button>
          <button
            type="button"
            aria-label={`Level ${level} 삭제`}
            className="text-destructive hover:text-destructive/80 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
            disabled={!canDelete}
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>

        {/* targets — clicking this area also opens edit */}
        <button
          type="button"
          className="w-full text-left px-4 pt-3 pb-4 pl-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          onClick={onClick}
        >
          <div className="space-y-1">
            {draft.isPrimary ? (
              <>
                <div className="flex items-center gap-2">
                  {primaryMember && <MemberAvatar member={primaryMember} />}
                  <p className="text-sm">{primaryName ?? "담당자 미지정"}</p>
                  <Badge variant="default" className="scale-[0.7] origin-left">primary</Badge>
                </div>
                {!primaryName && (
                  <p className="text-xs text-destructive">
                    스케줄 관리에서 로테이션을 설정해주세요.
                  </p>
                )}
              </>
            ) : draft.memberIds.length === 0 ? (
              <p className="text-sm text-destructive">대상 멤버를 선택해주세요</p>
            ) : (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {draft.memberIds.map((id) => (
                  <span key={id} className="inline-flex items-center gap-1.5 text-sm">
                    <MemberAvatar member={memberInfo(id)} />
                    {memberName(id)}
                  </span>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              재시도: {draft.max_attempts}회, 간격: {draft.interval_minutes}분
            </p>
          </div>
        </button>
      </div>
    </div>
  );
}

// ── EditStepModal ────────────────────────────────────────────────────────────

interface EditStepModalProps {
  open: boolean;
  onClose: () => void;
  initial: StepDraft | null;
  selectableMembers: SelectableMember[];
  primaryName: string | null;
  onSave: (draft: StepDraft) => void;
  mode: "add" | "edit";
}

function EditStepModal({
  open,
  onClose,
  initial,
  selectableMembers,
  primaryName,
  onSave,
  mode,
}: EditStepModalProps) {
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [intervalMin, setIntervalMin] = useState(2);
  const [memberIds, setMemberIds] = useState<number[]>([]);

  // add 모드는 항상 멤버 step. edit 모드에서 primary step이면 멤버 선택 UI를 숨긴다.
  const isPrimaryStep = mode === "edit" && !!initial?.isPrimary;

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setMaxAttempts(initial.max_attempts);
      setIntervalMin(initial.interval_minutes);
      setMemberIds([...initial.memberIds]);
    } else {
      setMaxAttempts(3);
      setIntervalMin(2);
      setMemberIds([]);
    }
  }, [open, initial]);

  function toggleMember(id: number, checked: boolean) {
    setMemberIds((prev) => (checked ? [...prev, id] : prev.filter((m) => m !== id)));
  }

  function handleSave() {
    // 멤버 step(비 primary)은 최소 1명 필요 — 미선택 시 토스트로 알리고 생성/저장 자체를 막는다.
    if (!isPrimaryStep && memberIds.length === 0) {
      toast({
        variant: "destructive",
        title: "대상 멤버를 선택해주세요",
        description: "최소 한 명의 대상 멤버를 선택해야 단계를 추가할 수 있습니다.",
      });
      return;
    }
    const base = initial ?? makeMemberDraftBase();
    onSave({
      ...base,
      isPrimary: isPrimaryStep,
      memberIds: isPrimaryStep ? [] : memberIds,
      max_attempts: maxAttempts,
      interval_minutes: intervalMin,
    });
    onClose();
  }

  function makeMemberDraftBase(): StepDraft {
    return { uid: newUid(), isPrimary: false, memberIds: [], interval_minutes: intervalMin, max_attempts: maxAttempts };
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "add" ? "에스컬레이션 단계 추가" : "에스컬레이션 단계 수정"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {isPrimaryStep ? (
            <div className="border rounded-lg p-3 bg-muted/30 text-sm">
              <div className="flex items-center gap-2">
                <p className="font-medium">{primaryName ?? "담당자 미지정"}</p>
                <Badge variant="default" className="scale-[0.7] origin-left">primary</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Level 1은 스케줄 로테이션의 primary 담당자에게 자동 발송됩니다.
                <br />
                대상은 변경할 수 없습니다.
              </p>
            </div>
          ) : (
            <div className="border rounded-lg p-3 bg-muted/30">
              <p className="text-xs text-muted-foreground mb-2">
                대상 멤버 (스케줄에 포함되지 않은 팀 멤버만 선택 가능)
              </p>
              {selectableMembers.length === 0 ? (
                <p className="text-sm text-muted-foreground">선택 가능한 멤버가 없습니다.</p>
              ) : (
                <div className="space-y-1 max-h-60 overflow-y-auto">
                  {selectableMembers.map((m) => (
                    <label key={m.member_id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={memberIds.includes(m.member_id)}
                        onCheckedChange={(v) => toggleMember(m.member_id, !!v)}
                      />
                      <span className="text-sm">
                        {m.name}
                        <span className="text-xs text-muted-foreground ml-1">({m.email})</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* step-level retry / interval */}
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div>
              <label htmlFor="max-attempts" className="text-xs text-muted-foreground block mb-1">
                재시도 횟수
              </label>
              <input
                id="max-attempts"
                type="number"
                min={1}
                value={maxAttempts}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!Number.isNaN(v)) setMaxAttempts(Math.max(1, v));
                }}
                className="w-full border rounded px-2 py-1 text-sm bg-background"
              />
            </div>
            <div>
              <label htmlFor="interval-minutes" className="text-xs text-muted-foreground block mb-1">
                간격 (분)
              </label>
              <input
                id="interval-minutes"
                type="number"
                min={1}
                value={intervalMin}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!Number.isNaN(v)) setIntervalMin(Math.max(1, v));
                }}
                className="w-full border rounded px-2 py-1 text-sm bg-background"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose}>
            취소
          </Button>
          <Button onClick={handleSave}>{mode === "add" ? "추가" : "저장"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── AlertEscalationPolicyPage ─────────────────────────────────────────────────

export function AlertEscalationPolicyPage() {
  const { selectedTeam } = useTeamStore();
  const teamId = selectedTeam?.id;
  const qc = useQueryClient();

  const [drafts, setDrafts] = useState<StepDraft[]>([]);
  const [editTarget, setEditTarget] = useState<StepDraft | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  // 자동 저장 제어용 ref — teamId 단위로 스코프해 저장 중 팀 전환 시 cross-team 오염을 방지한다.
  const policyExistsRef = useRef<Map<number, boolean>>(new Map()); // 팀별 POST(생성) vs PUT(수정) 결정
  const pendingPayloadRef = useRef<Map<number, AlertEscalationPolicyPayload>>(new Map()); // 팀별 저장 in-flight 중 들어온 최신 payload
  const hydratedTeamRef = useRef<number | null>(null); // 팀당 1회만 서버 상태로 시드 (자동 저장이 편집을 덮어쓰지 않도록)

  // quiet hours state
  const [quietEnabled, setQuietEnabled] = useState(false);
  const [callQuietStart, setCallQuietStart] = useState("22:00");
  const [callQuietEnd, setCallQuietEnd] = useState("08:00");
  const [callResumeAt, setCallResumeAt] = useState("09:00");

  // clear drafts and close modals when team changes
  useEffect(() => {
    setDrafts([]);
    setAddOpen(false);
    setEditTarget(null);
    setQuietEnabled(false);
    setCallQuietStart("22:00");
    setCallQuietEnd("08:00");
    setCallResumeAt("09:00");
    // policyExistsRef/pendingPayloadRef는 teamId 키 Map이라 전역 리셋하지 않는다.
    // (이전 팀의 pending 저장은 해당 팀으로 그대로 완료되어 편집 유실을 막는다)
    hydratedTeamRef.current = null;
  }, [teamId]);

  // ── query ─────────────────────────────────────────────────────────────────
  const { data: policy, isLoading } = useQuery<AlertEscalationPolicyResponse | null>({
    queryKey: ["escalation-policy", teamId],
    queryFn: async () => {
      if (!teamId) return null;
      try {
        return await getEscalationPolicy(teamId);
      } catch (error) {
        if (error instanceof ApiError && error.statusCode === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!teamId,
  });

  const { data: selectableMembers = [] } = useQuery<SelectableMember[]>({
    queryKey: ["escalation-selectable-members", teamId],
    queryFn: () => getSelectableMembers(teamId!),
    enabled: !!teamId,
  });

  const { data: primaryMember } = useQuery<SelectableMember | null>({
    queryKey: ["escalation-primary-member", teamId],
    queryFn: () => getCurrentPrimary(teamId!),
    enabled: !!teamId,
  });

  const primaryName = primaryMember?.name ?? null;

  const memberInfoMap = useMemo(() => {
    const map = new Map<number, MemberInfo>();
    selectableMembers.forEach((m) =>
      map.set(m.member_id, { id: m.member_id, name: m.name, picture: m.picture }),
    );
    // selectableMembers에 없지만 현재 로테이션 primary인 멤버명도 표시 가능하도록 보강
    // (저장 후 스케줄 편입된 멤버의 #id 폴백 방지 — 부분 커버. 완전 커버는 후속 백엔드 확장 필요)
    if (primaryMember) {
      map.set(primaryMember.member_id, {
        id: primaryMember.member_id,
        name: primaryMember.name,
        picture: primaryMember.picture,
      });
    }
    return map;
  }, [selectableMembers, primaryMember]);

  const memberInfo = (id: number): MemberInfo =>
    memberInfoMap.get(id) ?? { id, name: `#${id}`, picture: null };
  const memberName = (id: number) => memberInfoMap.get(id)?.name ?? `#${id}`;

  const primaryMemberInfo: MemberInfo | null = primaryMember
    ? { id: primaryMember.member_id, name: primaryMember.name, picture: primaryMember.picture }
    : null;

  useEffect(() => {
    if (policy === undefined) return; // 아직 로딩 중
    if (teamId == null) return;
    if (hydratedTeamRef.current === teamId) return; // 이미 시드 완료 — 자동 저장의 캐시 갱신이 편집을 덮어쓰지 않도록
    if (policy) {
      const sorted = [...policy.steps].sort((a, b) => a.step - b.step);
      setDrafts(sorted.map((s, i) => toDraft(s, newUid(), i === 0)));
      if (policy.call_quiet_start && policy.call_quiet_end && policy.call_resume_at) {
        setQuietEnabled(true);
        setCallQuietStart(policy.call_quiet_start.slice(0, 5));
        setCallQuietEnd(policy.call_quiet_end.slice(0, 5));
        setCallResumeAt(policy.call_resume_at.slice(0, 5));
      } else {
        setQuietEnabled(false);
      }
      policyExistsRef.current.set(teamId, true);
    } else {
      // policy === null: 신규 정책 — Level 1 primary step을 기본 제공
      setDrafts([makePrimaryDraft()]);
      setQuietEnabled(false);
      policyExistsRef.current.set(teamId, false);
    }
    hydratedTeamRef.current = teamId;
  }, [policy, teamId]);

  // ── mutations ─────────────────────────────────────────────────────────────
  // 저장은 (teamId, payload)를 함께 받아 콜백이 현재 렌더의 클로저 teamId가 아닌
  // 저장 시점의 팀을 정확히 참조하도록 한다(저장 중 팀 전환 대비).
  const saveMut = useMutation({
    mutationFn: async ({ teamId: tid, payload }: { teamId: number; payload: AlertEscalationPolicyPayload }) => {
      // 서버 policy 대신 팀별 ref로 판단 — 방금 생성된 정책도 refetch 전에 즉시 PUT으로 전환(409 race 방지)
      return policyExistsRef.current.get(tid)
        ? upsertEscalationPolicy(tid, payload)
        : createEscalationPolicy(tid, payload);
    },
    onSuccess: (data, { teamId: tid }) => {
      policyExistsRef.current.set(tid, true);
      // refetch 없이 캐시만 갱신 — drafts 재시드(uid churn)를 피한다. 키는 응답의 team_id로.
      if (data) qc.setQueryData(["escalation-policy", data.team_id], data);
    },
    onSettled: () => {
      // 저장 중 들어온 대기 payload를 발사한다. 전역 isPending 특성상 방금 끝난 팀이 아닌
      // 다른 팀 payload가 큐잉됐을 수 있으므로, 특정 팀만이 아니라 모든 대기 팀을 비운다.
      const pending = pendingPayloadRef.current;
      if (pending.size === 0) return;
      const entries = Array.from(pending.entries());
      pending.clear();
      for (const [tid, payload] of entries) {
        saveMut.mutate({ teamId: tid, payload });
      }
    },
  });

  // 현재 quiet hours state 스냅샷 (핸들러에서 변경분과 합쳐 persist에 전달)
  function currentQuiet(): QuietSnapshot {
    return { enabled: quietEnabled, start: callQuietStart, end: callQuietEnd, resumeAt: callResumeAt };
  }

  function buildPayload(nextDrafts: StepDraft[], q: QuietSnapshot): AlertEscalationPolicyPayload {
    return {
      steps: nextDrafts.map((d, i) => toApiStep(d, i)),
      call_quiet_start: q.enabled ? q.start : null,
      call_quiet_end: q.enabled ? q.end : null,
      call_resume_at: q.enabled ? q.resumeAt : null,
    };
  }

  // 커밋 동작마다 호출되는 자동 저장. in-flight면 최신값만 큐잉.
  function persist(nextDrafts: StepDraft[], q: QuietSnapshot) {
    if (!teamId || isLoading) return;
    const payload = buildPayload(nextDrafts, q);
    // 방어: 빈 대상 step은 모달 검증에서 차단되어 UI상 도달 불가. 만약을 대비해 저장만 건너뜀.
    if (payload.steps.length === 0 || payload.steps.some((s) => s.alert_targets.length === 0)) {
      return;
    }
    if (saveMut.isPending) {
      pendingPayloadRef.current.set(teamId, payload);
      return;
    }
    saveMut.mutate({ teamId, payload });
  }

  // ── dnd ───────────────────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = drafts.findIndex((d) => d.uid === active.id);
    const newIdx = drafts.findIndex((d) => d.uid === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(drafts, oldIdx, newIdx);
    // Level 1(primary)은 항상 맨 앞에 고정
    const primaryIdx = next.findIndex((d) => d.isPrimary);
    if (primaryIdx > 0) {
      const [primary] = next.splice(primaryIdx, 1);
      next.unshift(primary);
    }
    setDrafts(next);
    persist(next, currentQuiet());
  }

  // ── handlers ──────────────────────────────────────────────────────────────
  function applyAdd(draft: StepDraft) {
    const next = [...drafts, { ...draft, isPrimary: false }];
    setDrafts(next);
    persist(next, currentQuiet());
  }

  function deleteStep(uid: string) {
    const target = drafts.find((d) => d.uid === uid);
    if (!target || target.isPrimary) return; // primary step은 삭제 불가
    const next = drafts.filter((d) => d.uid !== uid);
    setDrafts(next);
    persist(next, currentQuiet());
  }

  function applyEdit(updated: StepDraft) {
    const next = drafts.map((d) => (d.uid === updated.uid ? updated : d));
    setDrafts(next);
    persist(next, currentQuiet());
  }

  // ── render ────────────────────────────────────────────────────────────────
  if (!teamId) {
    return (
      <div className="p-6 text-muted-foreground text-sm">
        팀을 먼저 선택해주세요.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl">
      {/* page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold">알람 및 에스컬레이션 정책 관리</h1>
          <p className="text-sm text-muted-foreground">{selectedTeam?.name}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* 자동 저장 상태 — 변경 시 즉시 저장됨 */}
          {saveMut.isPending ? (
            <span className="text-sm text-muted-foreground">저장 중…</span>
          ) : saveMut.isError ? (
            <span className="text-sm text-destructive">저장 실패</span>
          ) : saveMut.isSuccess ? (
            <span className="text-sm text-muted-foreground">저장됨</span>
          ) : null}
          <Button variant="outline" disabled={isLoading} onClick={() => setAddOpen(true)}>
            level 추가
          </Button>
        </div>
      </div>

      {/* save error */}
      {saveMut.error instanceof Error && (
        <p className="text-sm text-destructive mb-4">저장에 실패했습니다: {saveMut.error.message}</p>
      )}

      {/* loading */}
      {isLoading && <p className="text-sm text-muted-foreground">불러오는 중…</p>}

      {/* step cards */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={drafts.map((d) => d.uid)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-0">
            {drafts.map((draft, idx) => (
              <div key={draft.uid}>
                <StepCard
                  draft={draft}
                  level={idx + 1}
                  memberName={memberName}
                  memberInfo={memberInfo}
                  primaryName={primaryName}
                  primaryMember={primaryMemberInfo}
                  onDelete={() => deleteStep(draft.uid)}
                  onClick={() => setEditTarget(draft)}
                  canDelete={!draft.isPrimary}
                />
                {idx < drafts.length - 1 && (
                  <div className="flex justify-center py-2 text-muted-foreground">
                    <ChevronDown className="h-5 w-5" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* quiet hours */}
      <div className="mt-6 border rounded-lg p-4 bg-card">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <Checkbox
            checked={quietEnabled}
            disabled={isLoading}
            onCheckedChange={(v) => {
              const enabled = !!v;
              setQuietEnabled(enabled);
              persist(drafts, { ...currentQuiet(), enabled });
            }}
          />
          <span className="font-semibold text-sm">전화 억제 시간 설정 (Quiet Hours)</span>
        </label>
        {quietEnabled && (
          <div className="grid grid-cols-3 gap-3 mt-3 ml-6">
            <div>
              <label htmlFor="quiet-start" className="text-xs text-muted-foreground block mb-1">
                억제 시작
              </label>
              <input
                id="quiet-start"
                type="time"
                value={callQuietStart}
                disabled={isLoading}
                onChange={(e) => {
                  const start = e.target.value;
                  setCallQuietStart(start);
                  persist(drafts, { ...currentQuiet(), start });
                }}
                className="w-full border rounded px-2 py-1 text-sm bg-background"
              />
            </div>
            <div>
              <label htmlFor="quiet-end" className="text-xs text-muted-foreground block mb-1">
                억제 종료
              </label>
              <input
                id="quiet-end"
                type="time"
                value={callQuietEnd}
                disabled={isLoading}
                onChange={(e) => {
                  const end = e.target.value;
                  setCallQuietEnd(end);
                  persist(drafts, { ...currentQuiet(), end });
                }}
                className="w-full border rounded px-2 py-1 text-sm bg-background"
              />
            </div>
            <div>
              <label htmlFor="resume-at" className="text-xs text-muted-foreground block mb-1">
                전화 발송 시각
              </label>
              <input
                id="resume-at"
                type="time"
                value={callResumeAt}
                disabled={isLoading}
                onChange={(e) => {
                  const resumeAt = e.target.value;
                  setCallResumeAt(resumeAt);
                  persist(drafts, { ...currentQuiet(), resumeAt });
                }}
                className="w-full border rounded px-2 py-1 text-sm bg-background"
              />
            </div>
          </div>
        )}
      </div>

      {/* add modal */}
      <EditStepModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        initial={null}
        selectableMembers={selectableMembers}
        primaryName={primaryName}
        onSave={applyAdd}
        mode="add"
      />

      {/* edit modal */}
      <EditStepModal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        initial={editTarget}
        selectableMembers={selectableMembers}
        primaryName={primaryName}
        onSave={applyEdit}
        mode="edit"
      />
    </div>
  );
}
